import { useState, useEffect, useCallback } from 'react';
import { IconCalendarClock, IconInbox, IconBuilding, IconInboxCheck, IconBarChart } from './sidebarIcons';
import Sidebar from '../components/SidebarNext';
import { hasFunctionAccess } from '../permissionConfig';
import { API_BASE } from '../config';
import '../Accounts.css';

const THEME_KEY = 'acc_theme';

export default function AccountsLayout({ user, onLogout, children, basePath = '/invoicing' }) {
    const [theme, setTheme] = useState(() => localStorage.getItem(THEME_KEY) || 'normal');
    const [badgeCounts, setBadgeCounts] = useState({});

    const changeTheme = (next) => {
        setTheme(next);
        localStorage.setItem(THEME_KEY, next);
    };

    // Per-user "unvisited" counts (record-visits.js), for the two
    // queue pages rather than a bespoke mechanism per page — see AI-CHANGELOG
    // for the full design. Re-fetched on window focus (not just mount) so a
    // badge clears itself once the user comes back after visiting records,
    // without needing a live cross-component event bus.
    const loadBadgeCounts = useCallback(async () => {
        if (!user?.username) return;
        try {
            const res  = await fetch(`${API_BASE}/record-visits?action=badge_counts&username=${encodeURIComponent(user.username)}`);
            const data = await res.json();
            setBadgeCounts(data || {});
        } catch (e) { /* badges are best-effort, non-fatal */ }
    }, [user]);

    useEffect(() => {
        loadBadgeCounts();
        window.addEventListener('focus', loadBadgeCounts);
        return () => window.removeEventListener('focus', loadBadgeCounts);
    }, [loadBadgeCounts]);

    const NAV_BASE = [
        { to: `${basePath}/invoice-queue`, label: 'Invoice Queue', icon: <IconInbox />,          functionKey: 'invoice_queue', badgeCount: badgeCounts.invoice_queue },
        // No badgeCount on Sales Orders or Invoices — those pages list large,
        // long-lived sets (every SO / every invoice ever made), so an
        // "unvisited" count there is permanently large and not actionable,
        // unlike the two queues below/above it which represent real work-to-do.
        { to: `${basePath}/sales-orders`,   label: 'Sales Orders',  icon: <IconCalendarClock />,   functionKey: 'so_queue' },
        { to: `${basePath}/distributor-performa-invoice`, label: 'Proforma Invoice', icon: <IconInboxCheck />, functionKey: 'distributor_performa_invoice', badgeCount: badgeCounts.distributor_performa_invoice },
        { to: `${basePath}/invoices`,      label: 'Invoices',      icon: <IconBuilding />,        functionKey: 'invoices' },
        { to: `${basePath}/so-vs-invoice`, label: 'SO vs Invoice', icon: <IconBarChart />,        functionKey: 'so_vs_invoice' },
        // "Cancel SO" is no longer its own nav item — cancelling now happens
        // from inside Sales Orders, gated on the cancel_so permission there.
    ];
    const navItems = NAV_BASE.map(item => ({ ...item, locked: !hasFunctionAccess(user, 'invoicing', item.functionKey) }));

    return (
        <div
            data-acc-theme={theme === 'normal' ? undefined : theme}
            style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: 'var(--acc-page-bg, #FAF8FC)' }}
        >
            <Sidebar navItems={navItems} sectionLabel="Invoicing" homeHref="/landing" theme={theme} onThemeChange={changeTheme} user={user} domainKey="invoicing" />
            <div id="sbn-main" className="sbn-content"
                 style={{ flex: 1, minWidth: 0, padding: '28px 32px', overflowY: 'auto', height: '100%' }}>
                {children}
            </div>
        </div>
    );
}
